/**
 * Global singleton manager for Google One Tap to prevent multiple instances
 * from running simultaneously, which causes FedCM NotAllowedError.
 */

interface GoogleOneTapOptions {
  client_id: string;
  callback: (response: any) => void;
  prompt_parent_id?: string;
  error_callback?: (error: any) => void;
}

interface PromptCallback {
  (notification: any): void;
}

class GoogleOneTapManager {
  private static instance: GoogleOneTapManager;
  private isInitialized = false;
  private isPrompting = false;
  private currentOptions: GoogleOneTapOptions | null = null;
  private pendingRequests: Array<{
    options: GoogleOneTapOptions;
    promptCallback: PromptCallback;
    resolve: () => void;
    reject: (error: Error) => void;
  }> = [];

  private constructor() {}

  static getInstance(): GoogleOneTapManager {
    if (!GoogleOneTapManager.instance) {
      GoogleOneTapManager.instance = new GoogleOneTapManager();
    }
    return GoogleOneTapManager.instance;
  }

  /**
   * Initialize Google One Tap if not already initialized or if options have changed.
   * If already initialized with different options, queue the request.
   */
  async initialize(options: GoogleOneTapOptions, promptCallback: PromptCallback): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if browser supports FedCM
      if (!this.isFedCMSupported()) {
        reject(new Error('FedCM not supported in this browser/environment'));
        return;
      }

      // Check if Google library is available
      if (!window.google) {
        reject(new Error('Google library not loaded'));
        return;
      }

      // If already prompting, queue this request
      if (this.isPrompting) {
        console.log('Google One Tap: Another instance is already prompting, queueing request');
        this.pendingRequests.push({ options, promptCallback, resolve, reject });
        return;
      }

      // If initialized with the same client_id, reuse the existing initialization
      if (this.isInitialized && this.currentOptions?.client_id === options.client_id) {
        console.log('Google One Tap: Using existing initialization');
        this.promptWithCallback(promptCallback, resolve, reject);
        return;
      }

      // Initialize (or re-initialize with new options)
      try {
        console.log('Google One Tap: Initializing with client_id:', options.client_id);

        window.google.accounts.id.initialize({
          client_id: options.client_id,
          callback: options.callback,
          prompt_parent_id: options.prompt_parent_id,
          error_callback: (error: any) => {
            console.warn('Google One Tap error:', error);
            this.handleError(error);
            if (options.error_callback) {
              options.error_callback(error);
            }
          }
        });

        this.isInitialized = true;
        this.currentOptions = options;

        this.promptWithCallback(promptCallback, resolve, reject);
      } catch (error) {
        console.error('Failed to initialize Google One Tap:', error);
        this.handleError(error);
        reject(error instanceof Error ? error : new Error('Initialization failed'));
      }
    });
  }

  private promptWithCallback(
    promptCallback: PromptCallback,
    resolve: () => void,
    reject: (error: Error) => void
  ): void {
    try {
      this.isPrompting = true;

      window.google.accounts.id.prompt((notification: any) => {
        try {
          promptCallback(notification);
          resolve();
        } catch (error) {
          console.error('Error in prompt callback:', error);
          reject(error instanceof Error ? error : new Error('Prompt callback failed'));
        } finally {
          this.isPrompting = false;
          this.processNextRequest();
        }
      });
    } catch (error) {
      console.error('Failed to prompt Google One Tap:', error);
      this.isPrompting = false;
      this.handleError(error);
      reject(error instanceof Error ? error : new Error('Prompt failed'));
      this.processNextRequest();
    }
  }

  private processNextRequest(): void {
    if (this.pendingRequests.length > 0) {
      const nextRequest = this.pendingRequests.shift();
      if (nextRequest) {
        console.log('Google One Tap: Processing next queued request');
        // Use setTimeout to avoid immediate re-entry
        setTimeout(() => {
          this.initialize(nextRequest.options, nextRequest.promptCallback)
            .then(nextRequest.resolve)
            .catch(nextRequest.reject);
        }, 100);
      }
    }
  }

  private handleError(error: any): void {
    // Reset state on certain errors to allow retry
    if (
      error &&
      (error.type === 'NotAllowedError' ||
        error.toString().includes('NotAllowedError') ||
        error.toString().includes('only one navigator.credentials.get request'))
    ) {
      console.warn('Google One Tap: Resetting state due to NotAllowedError');
      this.isPrompting = false;
      // Don't reset isInitialized as the initialization itself is still valid
    }
  }

  private isFedCMSupported(): boolean {
    // Check if we're in a secure context (HTTPS or localhost)
    if (!window.isSecureContext && location.hostname !== 'localhost') {
      return false;
    }

    // Check for known problematic browsers/environments
    const userAgent = navigator.userAgent.toLowerCase();
    const isFirefox = userAgent.includes('firefox');
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');
    const isIOSWebView = /iphone|ipad|ipod/.test(userAgent) && !userAgent.includes('safari');

    // Disable on problematic browsers for now
    if (isFirefox || isSafari || isIOSWebView) {
      return false;
    }

    return true;
  }

  /**
   * Reset the manager state (useful for testing or when needing to reinitialize)
   */
  reset(): void {
    this.isInitialized = false;
    this.isPrompting = false;
    this.currentOptions = null;
    this.pendingRequests = [];
  }

  /**
   * Check if the manager is currently prompting
   */
  isCurrentlyPrompting(): boolean {
    return this.isPrompting;
  }

  /**
   * Get the number of pending requests
   */
  getPendingRequestCount(): number {
    return this.pendingRequests.length;
  }
}

// Export singleton instance
export const googleOneTapManager = GoogleOneTapManager.getInstance();

// Export type for external use
export type { GoogleOneTapOptions, PromptCallback };
